/**
 * Exports Module
 *
 * This module re-exports all important functions and types from the various modules.
 * It allows users to import directly from the main module file.
 *
 * Example:
 * import { reactiveStore } from 'as-components.v0.0.10.m.js';
 */

// Re-export from store.ts
export { reactiveStore } from './store/store.ts';

import { devlog, redirTo } from './util/utils.ts';
import { wcQry, wcQryAll, createElWithAttrs, replaceChildren, setHidden, setStepEnabled } from './util/wcUtil.ts';
import { bindElToProjData, bindHidden, bindAttrs, bindElToParams, bindElToSysData } from './components/base.ts';
import { appData } from './data/projectManagement.ts';
import { params } from './store/paramStore.ts';
import { sysData } from './store/sysDataStore.ts';
import { asApp, asConfigurator, asHeader, requireAsApp, requireAsConfigurator, requireAsHeader, whenAsApp, whenAsConfigurator, whenAsHeader, whenConfigurator } from './util/singletons.ts';
import { sendEv } from './data/events.ts';
import { addAsGlobals } from './asGlobals.ts';

/**
 * The library's loose helper functions, gathered into one object so they're
 * reachable the same way however the bundle was loaded:
 *
 *   import { AsUtils } from 'as-components';   // bundler / ES module
 *   window.asGlobals.utils                     // plain <script type="module">
 *
 * Both are the same object, and both get type hints from the generated .d.ts
 * (the global is typed by the `declare global` block in asGlobals.ts). Every
 * function is also exported individually below, so bundler users can import
 * just what they need and keep it tree-shakeable.
 */
export const AsUtils = {
  devlog,
  redirTo,
  // Element lookup: the three singletons by name, everything else via wcQry
  asApp,
  asConfigurator,
  asHeader,
  requireAsApp,
  requireAsConfigurator,
  requireAsHeader,
  whenAsApp,
  whenAsConfigurator,
  whenAsHeader,
  whenConfigurator,
  wcQry,
  wcQryAll,
  // Element creation: the imperative counterpart to a markup string
  createElWithAttrs,
  replaceChildren,
  // Dispatch a CustomEvent with the library's defaults
  sendEv,
  // Visibility and step panels
  setHidden,
  setStepEnabled,
  // The three data namespaces
  appData,
  params,
  sysData,
  // appData binding
  bindElToProjData,
  bindHidden,
  bindAttrs,
  // Paramate param binding
  bindElToParams,
  // System state (read-only)
  bindElToSysData,
}

// Same object on the runtime global, for consumers who only load the .js. The
// accessors sit at the top level too, so a plain script can call
// asGlobals.asApp() without going through utils.
addAsGlobals({
  utils: AsUtils,
  asApp, asConfigurator, asHeader,
  requireAsApp, requireAsConfigurator, requireAsHeader,
  whenAsApp, whenAsConfigurator, whenAsHeader, whenConfigurator,
  wcQry, wcQryAll, createElWithAttrs, replaceChildren, sendEv,
});

// Re-export types from as-components.d.ts
export type {
  IReactiveStore,
  DeriveSpec,
  DerivedStore
} from './as-components.d.ts';

// Re-export from as-api.ts
export { AsApi } from './features/serverApi/asApi.ts';
export type { UserData } from './features/serverApi/asApi.ts';

// License display helpers (shared by app headers and as-home user management)
export { getLicenseInfo, licenseDaysRemaining, parseLicenseDate, getSeatLabel, getUnallocatedSeats } from './components/apps-header/lib/license.ts';
export type { LicenseInfo, LicenseData } from './components/apps-header/lib/license.ts';
export { AsText } from './components/text-and-lang/as-text.ts';

// Re-export core internals for advanced usage/demos
// The store-building half of derive lives on currentProjectData.derive(...);
// deriveFrom is the standalone per-entry spec builder — see docs/dataBinding.md.
export { currentProjectData, deriveFrom } from './data/projectManagement.ts';
// The three data namespaces. Each reads directly and carries the same
// subscribe pair: subscribe(key | keys | aliases, cb) and subscribeAll(cb).
export { appData } from './data/projectManagement.ts';
export type { DeriveAppDataSpec, DeriveCustomDataSpec } from './data/projectManagement.ts';
export { wcQry, wcQryAll, createElWithAttrs, replaceChildren, setHidden, setStepEnabled } from './util/wcUtil.ts';
export type { AttrValue, ElContent } from './util/wcUtil.ts';
// Singleton element accessors: sync, throwing and promise forms. See util/singletons.ts.
export { asApp, asConfigurator, asHeader, requireAsApp, requireAsConfigurator, requireAsHeader, whenAsApp, whenAsConfigurator, whenAsHeader, whenConfigurator } from './util/singletons.ts';
export { AsEl, bindElToProjData, bindHidden, bindAttrs, bindElToParams, bindElToSysData } from './components/base.ts';
export type { ProjDataSyncTargets, BindElOpts, BindTarget } from './components/base.ts';
// Paramate params: read them, react to them, bind them onto elements.
// Writing still goes through <as-configurator>.setParamValue().
export { params } from './store/paramStore.ts';
export type { ParamData, ParamAliases, ResolvedParams } from './store/paramStore.ts';
// Undo history batching. batchedHistoryAction(label, fn) runs fn with a batch
// open so every configure/appData write inside collapses into one undo entry —
// for hand-rolled flows like upload + configure in one user action. The
// start/finish/cancel trio is the manual form underneath it.
export { batchedHistoryAction, startHistoryBatch, finishHistoryBatch, cancelHistoryBatch } from './features/history/undoHistory.ts';
// sysData is read-only from outside the library — the object refuses writes,
// and _setSysData is deliberately NOT exported.
export { sysData } from './store/sysDataStore.ts';
export type { SysData, SysDataKey, SysPhase, StepSelectorState } from './store/sysDataStore.ts';
export type { SysDataAPI } from './components/as-app/mixins/sysdata-mixin.ts';
export { AsConfigurator } from './components/paramate/as-configurator.ts';
export { AsGuiCtrls } from './components/viewport/as-gui-ctrls.ts';
export { AsCheckbox } from './components/inputs/as-checkbox.ts';
export { AsDropdown } from './components/inputs/as-dropdown.ts';
export { AsExportGroup } from './features/importExport/export/as-export-group.ts';
/** @deprecated Renamed to AsExportGroup, matching the <as-export-group> tag. */
export { AsExportGroup as AsExportStep } from './features/importExport/export/as-export-group.ts';
export { AsImportGroup } from './features/importExport/import/as-import-group.ts';
// Dim everything except one element. spotlight()/clearSpotlight() drive a
// shared instance, so a walkthrough needs no element in the app's markup.
export { AsSpotlight, spotlight, clearSpotlight } from './components/overlays/as-spotlight.ts';
export type { SpotlightTarget, SpotlightOptions } from './components/overlays/as-spotlight.ts';

// Re-export from as-app config
export { getAppInfo, getAppSettings, getAppConfig, getAppSuiteConfig, appList, filterCategories } from './components/as-app/lib/appConfig.ts';
export type { AppSettings, AppConfig, AppSuiteConfig } from './components/as-app/lib/appConfig.ts';
export { AsApp } from './components/as-app/as-app.ts';
export { TrHeader as AsHeader } from './components/apps-header/as-header.ts';
export type { ViewButtonName } from './components/apps-header/subcomps/as-view-set.ts';

// What a host environment provides, in one registration (window close, native
// save/open dialogs, a prefs store, settings). registerFileSaveProvider and
// registerFileImportProvider are one-section shorthands for registerHost.
export { registerHost, getHost, requestClose, CLOSE_REQUESTED_EVENT } from './features/host/host.ts';
export type { HostProvider, WindowHost, HostCheck } from './features/host/host.ts';
export type { PrefsBackend } from './features/host/prefsBackend.ts';

// Environment-agnostic file saving (native dialog in hosted envs, browser download otherwise)
export { saveFileAs, registerFileSaveProvider } from './features/host/fileSave.ts';
export type { FileSaveOptions, FileSaveResult, FileSaveProvider } from './features/host/fileSave.ts';

// File import providers for <as-file-input> (host envs replace the open/drop flow)
export { registerFileImportProvider } from './features/importExport/import/fileImportProvider.ts';
export type { FileImportProvider, FileImportContext, ImportedFile } from './features/importExport/import/fileImportProvider.ts';

// Re-export from events.ts
export { AS_EVENTS, isRealProjectSave, sendEv } from './data/events.ts';
export type { AsEventName, SendEvOptions, ProjectSaveReason, ProjectSavedDetail, ProjectLoadDetail } from './data/events.ts';
import { type AsEventName } from './data/events.ts';


export { HIDE_LICENSE_UX, isProduction } from './data/constants.ts';
// export { serverCodes } from './data/constants.ts';
// Note: localisedText exports are removed as they are still work in progress 

// Public data interfaces. The Internal section of data-interfaces.ts stays out of the barrel.
export { MESH_TYPES } from './data/data-interfaces.ts';
export type {
  FileInfo, FileInfoArr,
  PhpApiError,
  ChangelogEntry, Changelog, MultiChangelog,
  StoredNotification,
  MeshType, FileCategory, FileDataSchema,
  ExportStatusDetail, ExportCancelDetail, ExportUncancellableDetail, ExportProgressDetail,
} from './data/data-interfaces.ts';
